import * as Preact from "preact"
import Objdict from "objdict"

import App from "@fae/source/models/App.js"
import Unit from "@fae/source/models/Unit.js"
import Dev from "@fae/source/models/dev/Dev.js"
import Navigation from "@fae/source/engine/navigating/Navigation.js"

import unitSprites from "@fae/source/data/unitSprites.js"
import unitJobs from "@fae/source/data/unitJobs.js"

import SceneReadout from "@fae/source/views/dev/panes/SceneReadout.jsx"
import "@fae/source/views/dev/panes/DevInspectorUI.less"

function readDifferenceIntoHTML(difference) {
    return Object.keys(difference).map((key) => {
        const value = difference[key]
        if(value == undefined) return
        if(value.a != undefined
        && typeof value.a != "string"
        && Object.keys(value.a).length > 0) {
            const subobjectHTML = readDifferenceIntoHTML(value)
            if(subobjectHTML.filter((value) => value != null).length == 0) return null
            return (
                <div class="SubObject">
                    <div class="ObjectLabel">{key}</div>
                    {subobjectHTML}
                </div>
            )
        }
        return (
            <div class="DivergencePair">
                <div class="Label">{key}</div>
                <div class="EndstateValue">Saved: {JSON.stringify(value.a, null, 4) || "undefined"}</div>
                <div class="EndstateValue">Replayed: {JSON.stringify(value.b, null, 4) || "undefined"}</div>
            </div>
        )
    })
}

// === OMNI INSPECTION === //
export default class DevInspectorUI {
    render() {
        if(App.game?.chapter == undefined) return
        const selection = App.game.chapter.selection

        if(Dev.devSelection.selectedSavestateReport) {
            const inspectedObject = Dev.devSelection.selectedSavestateReport || {}
            return (
                <div class="DevSavestateInspector">
                    {readDifferenceIntoHTML(inspectedObject?.difference, inspectedObject?.endstateFromRecord, inspectedObject?.endstateFromActions)}
                </div>
            )
        }
        if(Dev.mode == "battle" || Dev.inProtoMode == false) {
            if(selection.selectedUnitKey != undefined) return <DevUnitInspectorUI/>
            if(Dev.devSelection.selectedSpotKey != undefined) return <DevSpotInspectorUI/>
            if(selection.hoveredUnitKey != undefined) return <DevUnitInspectorUI/>
            if(Dev.devSelection.hoveredSpotKey != undefined) return <DevSpotInspectorUI/>
        }
        if(Dev.mode == "geography") {
            if(Dev.devSelection.selectedEncounterKey != undefined
            || Dev.devSelection.hoveredEncounterKey != undefined) {
                if(Dev.devSelection.selectedVertexKey != undefined
                || Dev.devSelection.hoveredVertexKey != undefined) {
                    return <DevVertexInspectorUI/>
                }
                return <DevEncounterInspectorUI/>
            }
            if(Dev.devSelection.selectedBlockKeys != undefined
            || Dev.devSelection.hoveredBlockKeys != undefined) {
                return <DevTileInspectorUI/>
            }
        }
        if(Dev.mode == "scripts" && Dev.inProtoMode == true) {
            if(Dev.devSelection.selectedScene != undefined) {
                return <DevScriptInspectorUI/>
            }
        }
        return (
            <div class="DevInspector Nulled">
                Select an Object
            </div>
        )
    }
}

// === UNIT INSPECTION === //

const PROPERTY_HINTS = {
    "health": "The unit's remaining health in points.",
    "maxhealth": "The maximum number of health points for the unit",
    "key": "The unique id of the unit. It must be unique!!",
    "affiliation": "Which side the unit is on, be it good or bad or none.",
    "moverange": "How many tiles of movement the unit can take each turn.",
    "faceKey": "The face (or head) sprite, keyed from the spritesheet.",
    "bodyKey": "The body (or torso) sprite, keyed from the spritesheet.",
    "jobKey": "The job (or class) of the unit, keyed from a defined job list. Examples include sword or bow.",
    "randomJobKey": "A comma separated list of job keys to randomized between with each playthrough.",
}
const FIXED_ORDER_FOR_UNIT_PROPERTIES = [
    "key", // key
    "jobKey", // person
    "position", // location_on
    "health", "maxhealth", // favorite // cardio_load

    // --ITEMS-- front_hand
    // items

    // --STATS-- bomb sports_gymnastics explosion
    // element
    // power
    // attackrange
    // skillKeys

    // --DEFENSES-- shield
    // defense
    // resistance

    // --MOVEMENT-- directions_walk sprint air barefoot footprint
    // moverange
    // movetype
    // moveAudioKey
    // moveAnimationKey

    // --BEHAVIOR-- psychology
    "affiliation",
    // properties
    // state
    // type
    // role
    // intent

    // --SPRITES--
    // sprites
    // spriteKey
    // spriteSize
    // spriteHeadKey
    // spriteBodyKey
    // spriteAnimationKey

    // --DESCRIPTION--
    // name, title
    // jobName, subtitle
    // facts, introduction

    // NONE OF THE BELOW ARE AVAILABLE BY DEFAULT!!
    // SOME OF THE BELOW DON'T HAVE SUPPORTED VISUALIZATIONS YET!!
    // - not yet supported warnings!!! levelups, levelupcriteria, scripts, portraits, sprites, effects, energy (for different permutations of move/invoke, or 1 2 3)

    // --LEVELUPS--
    // level
    // levelups
    // levelingCriteria
    // experience, maxexperience

    // --PORTRAITS--
    // portraitKey
    // portraits

    // stack
    // effects
    // energy
    // isDead
    // isRemoved
    // actions

    // data
    // scripts
    // anything else

    // THESE COULD, ARGUABLY, BE HIGHER UP AND DEFAULT. BUT NOT FOR NOW.
    // direction
    // armor, maxarmor, armortype
    // unarmedItem
]
const DO_NOT_SHOW_VALUES = ["templateKey"]
const SUGGESTED_VALUES = {
    "jobKey": Object.keys(unitJobs),
    "bodyKey": Object.keys(unitSprites.bodies),
    "faceKey": Object.keys(unitSprites.faces),
    "weaponImageKey": Object.keys(unitSprites.weapons),
    "affiliation": ["good", "bad", "none", "neutral"]
}

class DevUnitInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        const selection = App.game.chapter?.selection
        const key = selection.selectedUnitKey || selection.hoveredUnitKey || Dev.devSelection.listHoveredUnitKey
        if(key == undefined) {
            const modedUnitsList = Dev.isEditingProto() ? Dev.loadedProtostage.units : App.game.chapter.stage.units
            return (
                <div class="DevInspector Nulled">
                    Select a Unit
                </div>
            )
        }
        let modedInspectedUnit
        let modedUnitList = []
        if(Dev.isEditingProto() == true) {
            modedUnitList = Dev.loadedProtostage.units
            modedInspectedUnit = Dev.loadedProtostage.units[key]
        } else {
            modedUnitList = App.game.chapter.stage.units
            modedInspectedUnit = App.game.chapter.stage.units[key]
        }
        if(modedInspectedUnit == undefined) {
            return (
                <div class="DevInspector Nulled">
                    Selected unit is not defined in this mode.
                    Select anew
                </div>
            )
        }
        const isSelected = (modedInspectedUnit.key == App.game.chapter.selection.selectedUnitKey)
        // Assemble a compound stand-in unit from the multiply selected units
        if(Dev.isEditingProto() == true
        && selection.multiselectedUnitKeys.length > 1) {
            const units = []
            let overlappingPropertiesKeys = Object.keys(Dev.loadedProtostage.units[key])
            selection.multiselectedUnitKeys.forEach((unitKey) => {
                let overlap = overlappingPropertiesKeys.filter(
                    (propertyKey) => {
                        return Object.keys(Dev.loadedProtostage.units[unitKey]).includes(propertyKey)
                    }
                )
                overlappingPropertiesKeys = overlap
                units.push(Dev.loadedProtostage.units[unitKey])
            })
            const compoundUnit = Objdict.map(Dev.loadedProtostage.units, (unit) => Unit.clean(Unit.copy(unit)))
            overlappingPropertiesKeys.forEach((propertyKey) => {
                selection.multiselectedUnitKeys.forEach((unitKey) => {
                    if(Dev.loadedProtostage.units[unitKey][propertyKey] != compoundUnit[propertyKey]) {
                        delete compoundUnit[propertyKey]
                    }
                })
            })
            compoundUnit.position = {
                "x": Math.min(...units.map((unit) => unit.position.x)) + " - " + Math.max(...units.map((unit) => unit.position.x)),
                "y": Math.min(...units.map((unit) => unit.position.y)) + " - " + Math.max(...units.map((unit) => unit.position.y)),
                "z": Math.min(...units.map((unit) => unit.position.z)) + " - " + Math.max(...units.map((unit) => unit.position.z)),
            }

            function setProperty(value, propertyKey) {
                if(value == "") {
                    selection.multiselectedUnitKeys.forEach((unitKey) => {
                        delete Dev.loadedProtostage.units[unitKey][propertyKey]
                    })
                } else {
                    selection.multiselectedUnitKeys.forEach((unitKey) => {
                        Dev.loadedProtostage.units[unitKey][propertyKey] = value
                    })
                }
            }
            return (
                <div class="DevUnitInspector" isSelected={isSelected}>
                    <div class="Header">
                        <div class="Name">{"Units"}</div>
                    </div>
                    <form>
                        <div>
                            {
                                Object.keys(compoundUnit.position).map((subkey) => {
                                    let propertyType = undefined
                                    if(isNaN(compoundUnit.position[subkey]) == false) propertyType = "number"
                                    return (
                                        <EditableSubproperty propertyKey={"position"}
                                            subpropertyKey={subkey} type={propertyType}
                                            onChangeCallback={(value) => {
                                                selection.multiselectedUnitKeys.forEach((unitKey) => {
                                                    Dev.loadedProtostage.units[unitKey].position[subkey] = value
                                                })
                                            }}
                                            label={"position" + "." + subkey} tooltip={PROPERTY_HINTS["position"]}
                                            modedUnit={compoundUnit}/>
                                    )
                                })
                            }
                        </div>
                        <NovelProperty
                            onChangeCallback={(value, propertyKey) => {setProperty(value, propertyKey)}}
                            modedUnit={compoundUnit}/>
                        {Object.keys(compoundUnit).sort().map((key) => {
                            let propertyType = undefined
                            if(typeof compoundUnit[key] == "string") propertyType = "text"
                            if(compoundUnit[key] == "") propertyType = "text"
                            if(compoundUnit[key] == null) propertyType = "text"
                            if(isNaN(compoundUnit[key]) == false) propertyType = "number"

                            if(key == "jobKey") {
                                return (
                                    <EditableProperty key={compoundUnit.key + key} propertyKey={key} type={propertyType}
                                        label={key} tooltip={PROPERTY_HINTS[key]}
                                        onChangeCallback={(newKey) => {
                                            setProperty(value, key)
                                            selection.multiselectedUnitKeys.forEach((unitKey) => {
                                                Dev.cacheUnitReferredProperties({"unit": Dev.loadedProtostage.units[unitKey]})
                                            })
                                        }}
                                        modedUnit={compoundUnit} modedList={modedUnitList}/>
                                )
                            }
                            if(propertyType != undefined) {
                                return (
                                    <EditableProperty key={compoundUnit.key + key} propertyKey={key} type={propertyType}
                                        label={key} tooltip={PROPERTY_HINTS[key]}
                                        onChangeCallback={(value) => {setProperty(value, key)}}
                                        modedUnit={compoundUnit}/>
                                )
                            }
                        })}
                    </form>
                </div>
            )
        }
        const unitPropertiesKeys = Object.keys(modedInspectedUnit)
        return (
            <div class="DevUnitInspector" isSelected={isSelected}>
                <div class="Header">
                    <div class="Name">{modedInspectedUnit.name || "Unit"}</div>
                    <div class="Key">(#{(modedInspectedUnit.key + "").split("-")[0]})</div>
                </div>
                <form>
                    {FIXED_ORDER_FOR_UNIT_PROPERTIES.map((key) => {
                        if(DO_NOT_SHOW_VALUES.includes(key)) return
                        let propertyType = undefined
                        const unitProperty = modedInspectedUnit[key]
                        if(unitProperty == "") propertyType = "text"
                        if(unitProperty == null) propertyType = "text"
                        if(isNaN(unitProperty) == false) propertyType = "number"

                        if(key == "key") {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    onChangeCallback={(newKey) => {App.game.chapter.selection.selectedUnitKey = newKey; Dev.cacheUnitReferredProperties({"unit": modedInspectedUnit})}}
                                    modedUnit={modedInspectedUnit} modedList={modedUnitList}/>
                            )
                        }
                        if(key == "jobKey") {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    onChangeCallback={(newKey) => {Dev.cacheUnitReferredProperties({"unit": modedInspectedUnit})}}
                                    modedUnit={modedInspectedUnit} modedList={modedUnitList}/>
                            )
                        }
                        if(typeof unitProperty == "string"
                        || isNaN(unitProperty) == false
                        || unitProperty == null
                        || unitProperty == undefined) {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    modedUnit={modedInspectedUnit}/>
                            )
                        }
                        // if it's not a simple data type like string or number
                        // then it's probably got sub-properties that need listing
                        return (
                            <div>
                                {
                                    Object.keys(unitProperty).map((subkey) => {
                                        let propertyType = undefined
                                        if(isNaN(unitProperty[subkey]) == false) propertyType = "number"
                                        return (
                                            <EditableSubproperty propertyKey={key}
                                                subpropertyKey={subkey} type={propertyType}
                                                label={key + "." + subkey} tooltip={PROPERTY_HINTS[key]}
                                                modedUnit={modedInspectedUnit}/>
                                        )
                                    })
                                }
                            </div>
                        )
                    })}
                    <NovelProperty
                        modedUnit={modedInspectedUnit}/>
                    {unitPropertiesKeys.sort().map((key) => {
                        if(FIXED_ORDER_FOR_UNIT_PROPERTIES.includes(key)) return
                        if(DO_NOT_SHOW_VALUES.includes(key)) return
                        let propertyType = undefined
                        if(typeof modedInspectedUnit[key] == "string") propertyType = "text"
                        if(modedInspectedUnit[key] == "") propertyType = "text"
                        if(modedInspectedUnit[key] == null) propertyType = "text"
                        if(isNaN(modedInspectedUnit[key]) == false) propertyType = "number"

                        if(propertyType != undefined) {
                            return (
                                <EditableProperty key={modedInspectedUnit.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    modedUnit={modedInspectedUnit}/>
                            )
                        }
                    })}
                </form>
            </div>
        )
    }
}

// === SCRIPT INSPECTION === //

export class DevScriptInspectorUI {
    render() {
        if(Dev.loadedScript == undefined) {
            if(history.state == null
            || history.state.dataFolder == undefined) {
                return (
                    <div class="DevInspector">
                        <span onClick={() => Dev.readScriptFile()}>
                            Please load a script
                        </span>
                    </div>
                )
            } else {
                Dev.readScriptFile()
                return
            }
        }
        return (
            <div class="DevScriptInspector">
                <SceneReadout/>
            </div>
        )
    }
}

// === SPOT INSPECTION === //

class DevSpotInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        if(Dev.devSelection.selectedSpotKey == undefined
        && Dev.devSelection.hoveredSpotKey == undefined) {
            return (
                <div class="DevInspector Nulled">
                    Select a Spot
                </div>
            )
        }
        const inspectedObject = Dev.loadedProtostage.world.spots[Dev.devSelection.selectedSpotKey || Dev.devSelection.hoveredSpotKey]
        if(inspectedObject == undefined) return
        return (
            <div class="DevSpotInspector" isSelected={Dev.devSelection.selectedSpotKey != undefined}>
                <div class="Header">
                    <div class="Name">{inspectedObject?.tag || "Spot"}</div>
                </div>
                <form>
                    {Object.keys(inspectedObject).sort().map((propertyKey) => {
                        let propertyType = undefined
                        if(inspectedObject[propertyKey] == "") propertyType = "text"
                        if(inspectedObject[propertyKey] == null) propertyType = "text"
                        if(isNaN(inspectedObject[propertyKey]) == false) propertyType = "number"

                        if(propertyKey == "key") {
                            return (
                                <EditableProperty key={inspectedObject?.key + propertyKey} propertyKey={propertyKey} type={propertyType}
                                    label={propertyKey}
                                    onChangeCallback={(newKey) => {Dev.devSelection.selectedSpotKey = newKey}}
                                    modedUnit={inspectedObject} modedList={Dev.loadedProtostage.world.spots}/>
                            )
                        }
                        if(typeof inspectedObject[propertyKey] == "string"
                        || isNaN(inspectedObject[propertyKey]) == false) {
                            return (
                                <EditableProperty key={inspectedObject?.key + propertyKey} propertyKey={propertyKey} type={propertyType}
                                    label={propertyKey}
                                    modedUnit={inspectedObject}/>
                            )
                        }
                        // if it's not a simple data type like string or number
                        // then it's probably got sub-properties that need listing
                        return (
                            <div>
                                {
                                    Object.keys(inspectedObject[propertyKey]).map((subkey) => {
                                        let propertyType = undefined
                                        if(isNaN(inspectedObject[propertyKey][subkey]) == false) propertyType = "number"
                                        return (
                                            <EditableSubproperty key={inspectedObject?.key + propertyKey + "." + subkey} propertyKey={propertyKey}
                                                subpropertyKey={subkey} type={propertyType}
                                                label={propertyKey + "." + subkey}
                                                modedUnit={inspectedObject}/>
                                        )
                                    })
                                }
                            </div>
                        )
                    })}
                    <NovelProperty
                        modedUnit={inspectedObject}/>
                </form>
            </div>
        )
    }
}

// === TILE INSPECTION === //
import {tileset} from "@fae/source/data/tilesets.js"

class DevTileInspectorUI {
    render() {
        const inspectedBlockKeys = Dev.devSelection.selectedBlockKeys || Dev.devSelection.hoveredBlockKeys
        return (
            <div class="DevTileInspector" isSelected={Dev.devSelection.selectedBlockKeys != undefined}>
                {inspectedBlockKeys.map((key) => {
                    const block = Dev.loadedProtostage.world.blocks[key]
                    return (
                        <div class="Block" onClick={() => {
                            Dev.devSelection.selectedBlockKeys = [key]
                        }}>
                            <div class="Header">
                                {key}
                            </div>
                            <BlockForm target={block} key={key}
                                onChangeCallback={() => Dev.redrawWorld()}
                                keyPropertyList={["channelKey", "tileKey"]}/>
                        </div>
                    )
                })}
            </div>
        )
    }
}
class BlockForm {
    render() {
        return (
            <form>
                {(this.props.keyPropertyList || Object.keys(this.props.target)).sort().map((key) => {
                    let propertyType = undefined
                    if(isNaN(this.props.target[key]) == false) propertyType = "number"
                    if(this.props.target[key] == null) propertyType = "text"
                    if(this.props.target[key] == "") propertyType = "text"

                    if(typeof this.props.target[key] == "string"
                    || isNaN(this.props.target[key]) == false
                    || this.props.target[key] == null
                    || this.props.target[key] == undefined) {
                        return (
                            <EditableProperty key={(this.props.key || this.props.target.key) + key}
                                propertyKey={key} type={propertyType}
                                onChangeCallback={this.props.onChangeCallback}
                                label={key}
                                modedUnit={this.props.target}/>
                        )
                    }
                    // if it's not a simple data type like string or number
                    // then it's probably got sub-properties that need listing
                    return (
                        <div>
                            {
                                Object.keys(this.props.target[key]).map((subkey) => {
                                    let propertyType = undefined
                                    if(isNaN(this.props.target[subkey]) == false) propertyType = "number"
                                    return (
                                        <EditableSubproperty
                                            key={(this.props.key || this.props.target.key) + key + subkey}
                                            propertyKey={key} subpropertyKey={subkey} type={propertyType}
                                            onChangeCallback={this.props.onChangeCallback}
                                            label={key + "." + subkey}
                                            modedUnit={this.props.target}/>
                                    )
                                })
                            }
                        </div>
                    )
                })}
                {this.props.target.colors?.map((color, index) => {
                    if(color.L == undefined) {
                        color.L = (Math.max(color.r, color.g, color.b) + Math.min(color.r, color.g, color.b)) / (255 * 2)
                    }
                    return(
                        <EditableSubproperty
                            key={(this.props.key || this.props.target.key) + "colors" + color.r + color.g + color.b}
                            propertyKey="colors" subpropertyKey={index} type="color"
                            onChangeCallback={this.props.onChangeCallback}
                            label={"L = " + color.L.toFixed(3) + ", colors." + index}
                            modedUnit={this.props.target}/>
                    )
                })}
                <div class="Button" onClick={() => {
                    if(this.props.target.colors == undefined) {
                        this.props.target.colors = []
                    }
                    this.props.target.colors.push({
                        "r": 0,
                        "g": 0,
                        "b": 0,
                        "L": 0,
                    })
                }}>
                    + Add color stop
                </div>
                {this.paletteFromImageButton}
            </form>
        )
    }
    get paletteFromImageButton() {
        if(tileset[this.props.target.tileKey]?.image != undefined
        || tileset[this.props.target.tileKey]?.images != undefined) {
            return (
                <div class="Button" onClick={() => {
                    this.props.target.colors = getPalette(tileset[this.props.target.tileKey].image || tileset[this.props.target.tileKey].images[1])
                }}>
                    - Set colors from image
                </div>
            )
        }
    }
}
function getPalette(image) {
    const canvas = document.createElement("canvas")
    canvas.context = canvas.getContext("2d")
    canvas.width = image.width
    canvas.height = image.height

    canvas.context.drawImage(image, 0, 0)

    const imagePalette = []
    const pixels = canvas.context.getImageData(0, 0, canvas.width, canvas.height)
    for(let index = 0; index < pixels.data.length; index += 4) {
        const sampledColor = {
            "r": pixels.data[index + 0],
            "g": pixels.data[index + 1],
            "b": pixels.data[index + 2],
            "a": pixels.data[index + 3],
        }
        const matchedColor = imagePalette.find((color) => {
            return color.r == sampledColor.r
                && color.g == sampledColor.g
                && color.b == sampledColor.b
        })
        if(matchedColor == undefined) imagePalette.push(sampledColor)
    }

    imagePalette.forEach((color) => {
        color.L = (Math.max(color.r, color.g, color.b) + Math.min(color.r, color.g, color.b)) / (255 * 2)
    })
    imagePalette.sort((a,b) => a.L - b.L)

    return imagePalette
}

// === ENCOUNTER INSPECTION === //

class DevVertexInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        const inspectedObject = Dev.loadedProtostage.world.encounters[Dev.devSelection.selectedEncounterKey || Dev.devSelection.hoveredEncounterKey]?.points?.find((vertex) => {
                return vertex.key == Dev.devSelection.selectedVertexKey
                    || vertex.key == Dev.devSelection.hoveredVertexKey
            })
        if(inspectedObject == undefined) {
            return (
                <div class="DevInspector Nulled">
                    Select a Vertex
                </div>
            )
        }
        return (
            <div class="DevVertexInspector" isSelected={Dev.devSelection.selectedVertexKey != undefined}>
                <div class="Header">
                    <div class="Name">{inspectedObject?.key || "Vertex"}</div>
                </div>
                <form>
                    {Object.keys(inspectedObject).sort().map((key) => {
                        let propertyType = undefined
                        if(isNaN(inspectedObject[key]) == false) propertyType = "number"
                        if(inspectedObject[key] == null) propertyType = "text"
                        if(inspectedObject[key] == "") propertyType = "text"

                        if(typeof inspectedObject[key] == "string"
                        || isNaN(inspectedObject[key]) == false) {
                            return (
                                <EditableProperty key={inspectedObject?.key + key} propertyKey={key} type={propertyType}
                                    label={key} tooltip={PROPERTY_HINTS[key]}
                                    modedUnit={inspectedObject}/>
                            )
                        }
                    })}
                </form>
            </div>
        )
    }
}
class DevEncounterInspectorUI {
    render() {
        if(App.game == undefined) return
        if(Dev.isInspectorShowing() != true) return
        if(Dev.devSelection.selectedEncounterKey == undefined
        && Dev.devSelection.hoveredEncounterKey == undefined) {
            return (
                <div class="DevInspector Nulled">
                    Select an Encounter
                </div>
            )
        }
        const inspectedEncounterKey = Dev.devSelection.selectedEncounterKey || Dev.devSelection.hoveredEncounterKey
        const inspectedObject = Dev.loadedProtostage.world.encounters[inspectedEncounterKey] || {}
        return (
            <div class="DevEncounterInspector" isSelected={Dev.devSelection.selectedEncounterKey != undefined}>
                <div class="Header">
                    <div class="Name">{inspectedObject?.key || "Encounter"}</div>
                </div>
                <form>
                    {Object.keys(inspectedObject).sort().map((key) => {
                        let propertyType = undefined
                        if(inspectedObject[key] == null) propertyType = "text"
                        if(inspectedObject[key] == "") propertyType = "text"
                        if(isNaN(inspectedObject[key]) == false) propertyType = "number"

                        if(key == "key") {
                            return (
                                <EditableProperty key={inspectedObject?.key + key}
                                    propertyKey={key} type={propertyType} label={key}
                                    onChangeCallback={(newKey) => {Dev.devSelection.selectedEncounterKey = newKey}}
                                    modedUnit={inspectedObject} modedList={Dev.loadedProtostage.world.encounters}/>
                            )
                        }
                        if(typeof inspectedObject[key] == "string"
                        || isNaN(inspectedObject[key]) == false) {
                            return (
                                <EditableProperty key={inspectedObject?.key + key}
                                    propertyKey={key} type={propertyType} label={key}
                                    modedUnit={inspectedObject}/>
                            )
                        }
                    })}
                </form>
            </div>
        )
    }
}

// === GENERAL UTILITIES === //

class EditableProperty {
    render() {
        if(this.list != undefined) {
            return (
                <label class="EditableProperty" key={this.props.key} isGrayedOut={this.isGrayedOut}>
                    <datalist id={this.props.label + "SuggestedValues"}>
                        {this.list.map((suggestion, suggestionkey) =>
                            <option key={suggestionkey} value={suggestion}>
                                {suggestion}
                            </option>
                        )}
                    </datalist>
                    <div class="Label" title={this.props.tooltip}>
                        <span class="Text">{this.props.label}</span>
                    </div>
                    <input class="Value" key={this.props.label} type={this.type}
                        defaultValue={this.defaultValue} list={this.props.label + "SuggestedValues"}
                        onChange={this.onChange} onKeyDown={this.onKeyDown}/>
                </label>
            )
        }
        return (
            <label class="EditableProperty" key={this.props.key}>
                <div class="Label" title={this.props.tooltip}>
                    <span class="Text">{this.props.label}</span>
                </div>
                <input class="Value" key={this.viewKey + "_input"} type={this.type}
                    defaultValue={this.defaultValue} list={this.list}
                    onChange={this.onChange} onKeyDown={this.onKeyDown}/>
            </label>
        )
    }
    get viewKey() {
        return this.props.key
    }
    get list() {
        if(SUGGESTED_VALUES[this.props.propertyKey] != undefined) {
            return SUGGESTED_VALUES[this.props.propertyKey]
        }
        return undefined
    }
    get type() {
        if(this.props.type == "number") {
            return "number"
        }
        if(this.props.type == "color") {
            return "color"
        }
        return "text"
    }
    get defaultValue() {
        const value = this.props.modedUnit[this.props.propertyKey]
        if(this.props.type == "color"
        && value.r != undefined) {
            return "#" + (value.r & 0xFF).toString(16).padStart(2,0) + (value.g & 0xFF).toString(16).padStart(2,0) + (value.b & 0xFF).toString(16).padStart(2,0)
        }
        // if(value == undefined) return "undefined"
        // if(value == null) return "null"
        // if(value == "") return "empty-string"
        return value
    }
    get isGrayedOut() {
        return this.props.modedUnit[this.props.propertyKey] == undefined
            || this.props.modedUnit[this.props.propertyKey] == ""
            || this.props.modedUnit[this.props.propertyKey] == null
    }
    get onKeyDown() {
        return (event) => {
            event.stopPropagation()
        }
    }
    get onChange() {
        return (event) => {
            event.preventDefault()
            let changedInput = event.target.value
            if(Dev.isEditingProto() == true) {
                Dev.saveToUndo()
            }


            if(this.props.propertyKey == "key") {
                const oldKey = this.props.modedUnit.key
                this.props.modedList[changedInput] = this.props.modedUnit
                this.props.modedUnit[this.props.propertyKey] = changedInput
                delete this.props.modedList[oldKey]
            } else {
                if(this.props.type == "number") {
                    changedInput = parseInt(changedInput || 0)
                    if(isNaN(changedInput)) {
                        console.warn(event.target.value, "was expected to be a number, but is not a number.")
                    }
                }
                if(this.props.type == "color") {
                    const newColor = {
                        "r": parseInt(changedInput.slice(1,3), 16),
                        "g": parseInt(changedInput.slice(3,5), 16),
                        "b": parseInt(changedInput.slice(5,7), 16),
                    }
                    changedInput = newColor
                }

                if(changedInput == "") {
                    delete this.props.modedUnit[this.props.propertyKey]
                } else {
                    this.props.modedUnit[this.props.propertyKey] = changedInput
                }
            }

            if(this.props.onChangeCallback != undefined) this.props.onChangeCallback(changedInput)
            App.game.chapter.recalculateStage()
            App.game.chapter.saveStage()
            Dev.autosaveUnitsToFile()
        }
    }
}

class EditableSubproperty extends EditableProperty {
    get defaultValue() {
        const value = this.props.modedUnit[this.props.propertyKey][this.props.subpropertyKey]
        if(this.props.type == "color"
        && value.r != undefined) {
            return "#" + (value.r & 0xFF).toString(16).padStart(2,0) + (value.g & 0xFF).toString(16).padStart(2,0) + (value.b & 0xFF).toString(16).padStart(2,0)
        }
        return value
    }
    get viewKey() {
        return this.props.key + this.props.subpropertyKey
    }
    get onChange() {
        return (event) => {
            event.preventDefault()
            let changedInput = event.target.value
            if(Dev.isEditingProto() == true) {
                Dev.saveToUndo()
            }

            if(this.props.type == "number") {
                changedInput = parseInt(changedInput || 0)
                if(isNaN(changedInput)) {
                    console.warn(event.target.value, "was expected to be a number, but is not a number.")
                }
            }
            if(this.props.type == "color") {
                const newColor = {
                    "r": parseInt(changedInput.slice(1,3), 16),
                    "g": parseInt(changedInput.slice(3,5), 16),
                    "b": parseInt(changedInput.slice(5,7), 16),
                }
                changedInput = newColor
            }

            if(this.props.modedUnit[this.props.propertyKey] != undefined) {
                if(changedInput == "") {
                    delete this.props.modedUnit[this.props.propertyKey]
                } else {
                    this.props.modedUnit[this.props.propertyKey][this.props.subpropertyKey] = changedInput
                }

                if(this.props.onChangeCallback != undefined) this.props.onChangeCallback(changedInput)
                App.game.chapter.recalculateStage()
                App.game.chapter.saveStage()
                Dev.autosaveUnitsToFile()
            } else {
                console.log("Cannot write subproperty of a unit property that does not exist")
            }
        }
    }
}

class NovelProperty extends EditableProperty {
    render() {
        return (
            <label class="EditableProperty">
                <input class="Label" type="text"
                    onChange={this.onLabelChange} onKeyDown={this.onKeyDown}/>
                <input class="Value"
                    onChange={this.onChange} onKeyDown={this.onKeyDown}/>
            </label>
        )
    }
    get onChange() {
        return (event) => {
            event.preventDefault()

            this.newValue = event.target.value
            if(this.newLabel == undefined
            || this.newLabel === "") return

            if(Dev.isEditingProto() == true) {
                Dev.saveToUndo()
            }

            this.props.modedUnit[this.newLabel] = this.newValue || null

            if(this.props.onChangeCallback != undefined) this.props.onChangeCallback(changedInput, this.newLabel)
            App.game.chapter.recalculateStage()
            App.game.chapter.saveStage()
            Dev.autosaveUnitsToFile()
        }
    }
    get onLabelChange() {
        return (event) => {
            event.preventDefault()
            let changedLabel = event.target.value

            if(changedLabel === "") return
            this.newLabel = changedLabel
        }
    }
}
